/**
 * Login Page
 *
 * Branded login page for AgentsWork with multiple auth providers.
 * Uses purple/pink gradient theme consistent with portal.
 */

import { type Component, createSignal, Show } from 'solid-js';
import {
  error,
  loading,
  resetPassword,
  signInWithEmail,
  signInWithMagicLink,
  signInWithProvider,
  signUpWithEmail,
  supabase,
} from '../../lib/supabase';

type AuthMode = 'login' | 'signup' | 'magic-link' | 'forgot-password';

export const LoginPage: Component = () => {
  const [mode, setMode] = createSignal<AuthMode>('login');
  const [email, setEmail] = createSignal('');
  const [password, setPassword] = createSignal('');
  const [otpCode, setOtpCode] = createSignal('');
  const [magicLinkSent, setMagicLinkSent] = createSignal(false);
  const [resetSent, setResetSent] = createSignal(false);
  const [localError, setLocalError] = createSignal<string | null>(null);
  const [successMessage, setSuccessMessage] = createSignal<string | null>(null);

  const handleEmailSubmit = async (e: Event) => {
    e.preventDefault();
    setLocalError(null);
    setSuccessMessage(null);

    if (mode() === 'magic-link') {
      const { error } = await signInWithMagicLink(email());
      if (!error) {
        setMagicLinkSent(true);
      }
    } else if (mode() === 'forgot-password') {
      const { error } = await resetPassword(email());
      if (!error) {
        setResetSent(true);
      }
    } else if (mode() === 'signup') {
      const { error } = await signUpWithEmail(email(), password());
      if (!error) {
        setSuccessMessage('Check your email to confirm your account.');
      }
    } else {
      await signInWithEmail(email(), password());
    }
  };

  const handleOtpSubmit = async (e: Event) => {
    e.preventDefault();
    setLocalError(null);

    if (!supabase) {
      setLocalError('Supabase not configured');
      return;
    }

    const { error } = await supabase.auth.verifyOtp({
      email: email(),
      token: otpCode(),
      type: 'email',
    });

    if (error) {
      setLocalError(error.message);
    }
  };

  const handleOAuth = async (provider: 'google' | 'github') => {
    setLocalError(null);
    await signInWithProvider(provider);
  };

  return (
    <div class="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-950">
      <div class="w-full max-w-md p-8">
        {/* Logo & Title */}
        <div class="text-center mb-8">
          <div class="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl mb-4 shadow-lg shadow-purple-500/30">
            <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h1 class="text-3xl font-bold text-white mb-2">Aigents Work</h1>
          <p class="text-gray-400">
            {mode() === 'signup' ? 'Create your account' : 'Welcome back'}
          </p>
        </div>

        {/* Auth Card */}
        <div class="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-6 shadow-xl">
          {/* OAuth Buttons */}
          <div class="space-y-3 mb-6">
            <button
              onClick={() => handleOAuth('google')}
              disabled={loading()}
              class="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white hover:bg-gray-100 text-gray-800 font-medium rounded-xl transition-colors disabled:opacity-50"
            >
              <svg class="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </button>

            <button
              onClick={() => handleOAuth('github')}
              disabled={loading()}
              class="w-full flex items-center justify-center gap-3 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-xl transition-colors disabled:opacity-50"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
              </svg>
              Continue with GitHub
            </button>
          </div>

          {/* Divider */}
          <div class="relative my-6">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-gray-600"></div>
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-2 bg-gray-800/50 text-gray-400">Or continue with email</span>
            </div>
          </div>

          {/* Password Reset Sent */}
          <Show when={resetSent()}>
            <div class="text-center py-4">
              <div class="inline-flex items-center justify-center w-12 h-12 bg-green-600/20 rounded-full mb-3">
                <svg
                  class="w-6 h-6 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <p class="text-white font-medium">Check your email</p>
              <p class="text-gray-400 text-sm mt-1">We sent a password reset link to {email()}</p>
              <button
                onClick={() => {
                  setResetSent(false);
                  setMode('login');
                }}
                class="mt-4 text-purple-400 hover:text-purple-300 text-sm"
              >
                Back to login
              </button>
            </div>
          </Show>

          {/* Magic Link Sent - OTP Code Entry */}
          <Show when={magicLinkSent() && !resetSent()}>
            <div class="py-4">
              <div class="text-center mb-4">
                <div class="inline-flex items-center justify-center w-12 h-12 bg-purple-600/20 rounded-full mb-3">
                  <svg
                    class="w-6 h-6 text-purple-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <p class="text-white font-medium">Enter the code from your email</p>
                <p class="text-gray-400 text-sm mt-1">We sent a 6-digit code to {email()}</p>
              </div>

              <form onSubmit={handleOtpSubmit} class="space-y-4">
                <div>
                  <input
                    type="text"
                    value={otpCode()}
                    onInput={(e) => setOtpCode(e.currentTarget.value)}
                    placeholder="Enter 6-digit code"
                    required
                    maxLength={6}
                    class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white text-center text-2xl tracking-widest placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <Show when={localError()}>
                  <div class="p-3 bg-red-500/20 border border-red-500/50 rounded-xl">
                    <p class="text-red-400 text-sm">{localError()}</p>
                  </div>
                </Show>

                <button
                  type="submit"
                  disabled={loading() || otpCode().length !== 6}
                  class="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-purple-500/20"
                >
                  {loading() ? 'Verifying...' : 'Verify Code'}
                </button>
              </form>

              <div class="mt-4 text-center">
                <button
                  onClick={() => {
                    setMagicLinkSent(false);
                    setOtpCode('');
                  }}
                  class="text-gray-400 hover:text-white text-sm"
                >
                  Use a different email
                </button>
              </div>
            </div>
          </Show>

          {/* Email Form */}
          <Show when={!magicLinkSent() && !resetSent()}>
            <form onSubmit={handleEmailSubmit} class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1.5">Email</label>
                <input
                  type="email"
                  value={email()}
                  onInput={(e) => setEmail(e.currentTarget.value)}
                  placeholder="you@example.com"
                  required
                  class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <Show when={mode() !== 'magic-link' && mode() !== 'forgot-password'}>
                <div>
                  <div class="flex justify-between items-center mb-1.5">
                    <label class="block text-sm font-medium text-gray-300">Password</label>
                    <Show when={mode() === 'login'}>
                      <button
                        type="button"
                        onClick={() => setMode('forgot-password')}
                        class="text-xs text-purple-400 hover:text-purple-300"
                      >
                        Forgot password?
                      </button>
                    </Show>
                  </div>
                  <input
                    type="password"
                    value={password()}
                    onInput={(e) => setPassword(e.currentTarget.value)}
                    placeholder="Enter your password"
                    required
                    minLength={8}
                    class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </Show>

              {/* Error Message */}
              <Show when={error() || localError()}>
                <div class="p-3 bg-red-500/20 border border-red-500/50 rounded-xl">
                  <p class="text-red-400 text-sm">{error() || localError()}</p>
                </div>
              </Show>

              {/* Success Message */}
              <Show when={successMessage()}>
                <div class="p-3 bg-green-500/20 border border-green-500/50 rounded-xl">
                  <p class="text-green-400 text-sm">{successMessage()}</p>
                </div>
              </Show>

              <button
                type="submit"
                disabled={loading()}
                class="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-purple-500/20"
              >
                {loading() ? (
                  <span class="inline-flex items-center gap-2">
                    <svg class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      />
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Processing...
                  </span>
                ) : mode() === 'signup' ? (
                  'Create Account'
                ) : mode() === 'magic-link' ? (
                  'Send Login Code'
                ) : mode() === 'forgot-password' ? (
                  'Send Reset Link'
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
          </Show>

          {/* Mode Switcher */}
          <div class="mt-6 space-y-2 text-center text-sm">
            <Show when={mode() !== 'magic-link' && mode() !== 'forgot-password'}>
              <button
                onClick={() => setMode('magic-link')}
                class="text-gray-400 hover:text-white transition-colors"
              >
                Sign in with login code instead
              </button>
            </Show>
            <Show when={mode() === 'magic-link'}>
              <button
                onClick={() => setMode('login')}
                class="text-gray-400 hover:text-white transition-colors"
              >
                Sign in with password instead
              </button>
            </Show>
            <Show when={mode() === 'forgot-password'}>
              <button
                onClick={() => setMode('login')}
                class="text-gray-400 hover:text-white transition-colors"
              >
                Back to sign in
              </button>
            </Show>
            <div class="pt-2 border-t border-gray-700">
              <Show when={mode() === 'login' || mode() === 'magic-link'}>
                <span class="text-gray-400">Don't have an account?</span>
                <button
                  onClick={() => setMode('signup')}
                  class="text-purple-400 hover:text-purple-300 font-medium"
                >
                  Sign up
                </button>
              </Show>
              <Show when={mode() === 'signup' || mode() === 'forgot-password'}>
                <span class="text-gray-400">Already have an account?</span>
                <button
                  onClick={() => setMode('login')}
                  class="text-purple-400 hover:text-purple-300 font-medium"
                >
                  Sign in
                </button>
              </Show>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p class="mt-6 text-center text-sm text-gray-500">
          By continuing, you agree to our{' '}
          <a href="/terms" class="text-gray-400 hover:text-white">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="/privacy" class="text-gray-400 hover:text-white">
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
